import React, { useState, useMemo, useEffect, useContext, useRef, useCallback } from "react";
import NavBar from "../partials/NavBar";
import { GlobalContext } from "../GlobalContext";
import { parseData, filterRecords } from "../localParser";
import Utils from "../utils";

const RESULT_LIMIT = 300;

const LocalSearch = () => {
  const { username, logout, updateDatabase, toast } = useContext(GlobalContext);

  const [fileName, setFileName] = useState("");
  const [type, setType] = useState("");
  const [format, setFormat] = useState(null);
  const [records, setRecords] = useState([]);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Debounce the query so filtering large files stays responsive while typing.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 150);
    return () => clearTimeout(t);
  }, [query]);

  const loadText = useCallback((text, name) => {
    try {
      const parsed = parseData(text);
      if (parsed.type === "Unknown") {
        setError(
          "Could not detect the data type. Make sure this is a raw L2 .txt data file (ItemName, SkillName, NpcName, SysString/NpcString, HuntingZone, or EnchantedItemSkills)."
        );
        setType("");
        setRecords([]);
        setFormat(null);
      } else {
        setError("");
        setType(parsed.type);
        setRecords(parsed.records);
        setFormat(parsed.format);
      }
      setFileName(name || "");
      setQuery("");
    } catch (e) {
      setError("Failed to parse file: " + e.message);
    }
  }, []);

  const handleFiles = useCallback(
    (files) => {
      const file = files && files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => loadText(String(ev.target.result), file.name);
      reader.onerror = () => setError("Failed to read file.");
      reader.readAsText(file);
    },
    [loadText]
  );

  // Copy a cell's contents. Plain click copies the visible (rendered) text;
  // Alt+Click copies the raw original value (HTML tags, literal \n, etc.).
  const copyCell = useCallback(
    (e, raw) => {
      const value = e.altKey
        ? raw == null
          ? ""
          : String(raw)
        : e.currentTarget.innerText;
      if (!value) return;
      navigator.clipboard.writeText(value);
      if (toast) {
        toast.success(e.altKey ? "Copied raw content" : "Copied", { autoClose: 900 });
      }
    },
    [toast]
  );

  // If the pasted search text looks like "id=####" (optionally bracketed),
  // strip the "id=" and search by the number.
  const onSearchPaste = (e) => {
    const text = (e.clipboardData && e.clipboardData.getData("text")) || "";
    const m = text.match(/id=\[?([^\]\s]+)\]?/i);
    if (m) {
      e.preventDefault();
      setQuery(m[1]);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = () => setDragOver(false);

  const results = useMemo(
    () => filterRecords(records, debouncedQuery, RESULT_LIMIT),
    [records, debouncedQuery]
  );

  const isSkill = type === "Skill";
  const hasSecondary = !!(format && format.secondaryLabel);

  return (
    <div className="App">
      <h1>L2 Localization Tool</h1>
      <div className="search-container">
        <NavBar username={username} onLogout={logout} updateDatabase={updateDatabase} />
        <center>
          <h2>Local Data Search</h2>
        </center>
        <p className="local-search-hint">
          Upload a raw L2 <code>.txt</code> data file. It is parsed entirely in your
          browser &mdash; nothing is sent to the server. The data type is auto-detected.
        </p>

        <div
          className={`local-dropzone ${dragOver ? "dragover" : ""}`}
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          role="button"
          tabIndex={0}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,text/plain"
            style={{ display: "none" }}
            onChange={(e) => handleFiles(e.target.files)}
          />
          {fileName ? (
            <span>
              Loaded: <strong>{fileName}</strong> &mdash; click or drop to load another
            </span>
          ) : (
            <span>
              Click to choose a <strong>.txt</strong> file, or drag &amp; drop it here
            </span>
          )}
        </div>

        {error && <div className="local-error">{error}</div>}

        {type && (
          <>
            <div className="local-meta">
              Detected type: <span className="result-type-badge">{type}</span>
              &nbsp;&nbsp;&nbsp; Records: <strong>{records.length.toLocaleString()}</strong>
            </div>

            <div className="local-search-box">
              <input
                type="text"
                placeholder="Search by ID or text..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onPaste={onSearchPaste}
                autoFocus
              />
            </div>

            <div className="local-results-info">
              {debouncedQuery.trim()
                ? `Showing ${results.length}${
                    results.length === RESULT_LIMIT ? "+ (top " + RESULT_LIMIT + ")" : ""
                  } match${results.length === 1 ? "" : "es"}`
                : `Showing first ${results.length} of ${records.length.toLocaleString()}`}
            </div>

            <div className="local-table-wrap">
              <table className="local-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    {isSkill && <th>Lv / Sub</th>}
                    <th>{format.primaryLabel || "Value"}</th>
                    {hasSecondary && <th>{format.secondaryLabel}</th>}
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr key={i}>
                      <td
                        className="local-id local-copyable"
                        title="Click to copy"
                        onClick={(e) => copyCell(e, r.id)}
                      >
                        {r.id}
                      </td>
                      {isSkill && (
                        <td
                          className="local-id local-copyable"
                          title="Click to copy"
                          onClick={(e) =>
                            copyCell(
                              e,
                              `${r.level == null ? "" : r.level}${
                                r.sublevel ? " / " + r.sublevel : ""
                              }`
                            )
                          }
                        >
                          {r.level}
                          {r.sublevel ? " / " + r.sublevel : ""}
                        </td>
                      )}
                      <td
                        className="local-copyable local-l2"
                        title="Click to copy · Alt+Click for raw"
                        onClick={(e) => copyCell(e, r.primary)}
                      >
                        <span
                          className={Utils.getDescClass(r.primary || "")}
                          dangerouslySetInnerHTML={{ __html: Utils.doPreview(r.primary || "") }}
                        />
                      </td>
                      {hasSecondary && (
                        <td
                          className="local-copyable local-l2"
                          title="Click to copy · Alt+Click for raw"
                          onClick={(e) => copyCell(e, r.secondary)}
                        >
                          <span
                            className={Utils.getDescClass(r.secondary || "")}
                            dangerouslySetInnerHTML={{
                              __html: Utils.doPreview(r.secondary || ""),
                            }}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {results.length === 0 && <div className="local-noresults">No matches.</div>}
          </>
        )}
      </div>
    </div>
  );
};

export default LocalSearch;
